import './DesignerInspector.css';

import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Col, Form, Row, Table } from 'react-bootstrap'
import IconButton from '../common/IconButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import ServicePropertiesForm from './ServicePropertiesForm';
import { useDispatch, useSelector } from 'react-redux';
import { setEnvironmentVariables, updateUsedItem } from '../../slices/designerSlice';
import ApplicationForm from './ApplicationForm';
import { Link } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { setInspectingItem, updateItemLabel } from '../../slices/inspectorSlice';
import { getConnectionDetails, createQueryDetails } from '../../utils';
import { VizClient } from '../../api/VizClient';
import { useToasts } from '../../contexts/toasts';
import { memo } from 'react';
import UserItem from '../common/UserItem';
import { BdaApplication } from '../../models/BdaApplication';
import { RootState } from '../../store';
import { useReactFlow } from 'reactflow';
import { Flex, Tooltip, Badge as AntBadge, Select } from 'antd';
import { ModeBadge } from '../common/ModeBadge';
import { EnvironmentVariables } from '../../api/EnvironmentVariables';
import { EnvVariable } from '../../models/Environment';
import DatasetPreviewModal from '../generics/DatasetPreviewModal';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { Targets } from '../../api/Targets';
import { Target } from '../../models/Target';


type Props = {
  onClose: VoidFunction;
  onApplicationSubmit: (application: BdaApplication) => void;
  onInspectionCancel: React.MouseEventHandler<HTMLButtonElement>;
  onShowServiceMarkdown?: (serviceId: number) => void;
  onShowNotes?: (key: "service" | "workflow") => void;
  fromTemplate?: boolean;
}

function DesignerInspector({ onClose, onApplicationSubmit, onInspectionCancel, onShowServiceMarkdown, onShowNotes, fromTemplate }: Props) {
  console.log("[Inspector] re-render");
  const availableStorages = useSelector((state: RootState) => state.designer.availableStorages);
  const inspectingItem = useSelector((state: RootState) => state.inspector.inspectingItem);

  const { setNodes } = useReactFlow();
  const dispatch = useDispatch();

  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [show, setShow] = useState(false);
  const { showToast } = useToasts();

  const [serviceLabel, setServiceLabel] = useState<string>();
  const [targetOptions, setTargetOptions] = useState<any[]>([]);

  const envVarLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));
  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));
  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));

  useEffect(() => {
    if (inspectingItem) {
      setServiceLabel(inspectingItem.label || inspectingItem.name);
      dispatch(updateUsedItem(inspectingItem));
    }
    else setServiceLabel(undefined);
    // eslint-disable-next-line
  }, [inspectingItem]);

  const initEnvironmentVariables = useCallback(() => {
    EnvironmentVariables.getEnvironmentVariables()
      .then(
        ({ data }) => dispatch(setEnvironmentVariables((data as Array<EnvVariable>))),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('env-variables-error', message, `${envVarLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, dispatch, envVarLabels]);

  const initTargets = useCallback(() => {
    const params = new URLSearchParams();
    params.append("compact", "true");

    Targets.getTargets(params)
      .then(
        ({ data }) => {
          setTargetOptions((data as Array<Target>).map((target) => ({
            label: target.name!,
            description: target.description || "",
            value: target.id!,
          })))
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast(`targets-error`, message, `Targets Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(initEnvironmentVariables, [initEnvironmentVariables]);
  useEffect(initTargets, [initTargets]);

  const handleStorageChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const value = evt.target.value;
    const found = inspectingItem.service.outputs.findIndex((output: any) => output.id === inspectingItem.type);
    if (found < 0) return;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.outputs[found].targetStorage = +value;
    dispatch(setInspectingItem(newItem));
  }

  const handleCascadeColumnsChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = value;
    dispatch(setInspectingItem(newItem));
  }

  const handleClose = () => {
    setShow(false);
    setDatasetPreview(undefined);
  };

  const handleTargetChange = useCallback((value: string) => dispatch(setInspectingItem({ ...inspectingItem, targetId: value })), [inspectingItem, dispatch]);

  const openDatasetPreview = () => {
    setShow(true);
    const connectionDetails = getConnectionDetails(inspectingItem.datasource, undefined, inspectingItem.datasetFileType);
    const body = createQueryDetails(connectionDetails);

    VizClient.getDatasetPreview(body, connectionDetails.type)
      .then(
        ({ data: preview }) => setDatasetPreview(preview?.result?.data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${datasetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleSelectedChange = (index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.checked;
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const input = inspectingItem.service.inputs[found];
    const newColumns = cloneDeep(input.dataset.columns);
    newColumns[index].selected = value;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = newColumns;
    dispatch(setInspectingItem(newItem));
  };

  const handleServiceLabelChange = (evt: React.ChangeEvent<HTMLInputElement>) => setServiceLabel(evt.target.value);

  const handleServiceLabelUpdate = () => {
    dispatch(updateItemLabel(serviceLabel));
    setNodes(nodes => {
      const newNodes = [...nodes];
      const found = nodes.findIndex(n => n.id === inspectingItem.ref);
      newNodes[found].data.label = serviceLabel || inspectingItem.name;
      return newNodes;
    });
  }

  const handleAgentFieldChange = (field: string, value: string) => {
    setNodes(nodes => {
      const newNodes = [...nodes];
      const found = nodes.findIndex(n => n.id === inspectingItem.ref);
      newNodes[found].data[field] = value;
      return newNodes;
    });
  }

  const renderDatasetDetail = () => {
    return (
      <div className="p-2">
        <Table borderless>
          <thead>
            <tr>
              <th style={{ width: "40%" }}></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="col">Type</th>
              <td className="text-end">{inspectingItem.datasetFileType.type}</td>
            </tr>
            {inspectingItem.datasource && (
              <tr>
                <th scope="col">Data Source</th>
                <td className="text-end">
                  <Link to={`/datasources/${inspectingItem.datasource.id}`} target="_blank" rel="noopener noreferrer">{inspectingItem.datasource.name}</Link>
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {inspectingItem.columns?.length > 0 && (
          <>
            <h6 className="mb-1 text-muted">{datasetLabels.singular} Columns</h6>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-center">Type</th>
                </tr>
              </thead>
              <tbody>
                {inspectingItem.columns.map((col: any) => (
                  <tr key={col.name}>
                    <td>{col.name}</td>
                    <td className="text-center text-muted">
                      <small>{col.type}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {inspectingItem.datasetFileType.type === "table" && (
          <div className="text-center">
            <Button variant="light" size="sm" className="border px-4" onClick={openDatasetPreview}>
              <FontAwesomeIcon icon={solid('eye')} />
              <span className="ms-3">Show Preview</span>
            </Button>
          </div>
        )}

        <DatasetPreviewModal
          show={show}
          onClose={handleClose}
          preview={datasetPreview}
          title={`${inspectingItem.name || datasetLabels.singular} Preview`}
        />
      </div>
    )
  }

  const renderModelDetail = () => {
    return (
      <table className="table-transparent">
        <tbody>
          <tr>
            <th scope="col">Algorithm</th>
            <td>{inspectingItem.algorithmName}</td>
          </tr>
          {inspectingItem.inputs && (
            <tr>
              <th scope="col">Input Columns</th>
              <td>
                <div className="d-flex flex-wrap">
                  {inspectingItem.inputs.split(",").map((col: string) => (
                    <Badge key={col} bg="light" className="text-muted me-2 border">{col}</Badge>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {inspectingItem.trainingDataset && (
            <tr>
              <th scope="col">{datasetLabels.singular}</th>
              <td>
                <Link className="d-inline-block" to={`/datasets/${inspectingItem.trainingDataset.id}`} target="_blank" rel="noopener noreferer">
                  <div className="border rounded px-2 bg-light">
                    <FontAwesomeIcon icon={solid('database')} />
                    <span className="ms-2">{inspectingItem.trainingDataset.name}</span>
                  </div>
                </Link>
              </td>
            </tr>
          )}
          <tr>
            <th scope="col">{applicationLabels.singular}</th>
            <td>
              <Link className="d-inline-block" to={`/bda-applications/${inspectingItem.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                <div className="border rounded px-2 bg-light">
                  <FontAwesomeIcon icon={solid('laptop-code')} />
                  <span className="ms-2">{inspectingItem.createdByBdaId}</span>
                </div>
              </Link>
            </td>
          </tr>
          {inspectingItem.datasource && (
            <tr>
              <th scope="col">{datasourceLabels.singular}</th>
              <td className="text-end">
                <Link to={`/datasources/${inspectingItem.datasource.id}`} target="_blank" rel="noopener noreferrer">{inspectingItem.datasource.name}</Link>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }

  const selectAll = (allSelected: boolean) => () => {
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const input = inspectingItem.service.inputs[found];
    const newColumns = cloneDeep(input.dataset.columns);
    newColumns.filter((col: any) => !col.disabled).forEach((col: any) => col.selected = !allSelected);

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = newColumns;
    dispatch(setInspectingItem(newItem));
  }

  const renderAssetSettings = () => {
    if (inspectingItem.type.includes('output-dataset') || inspectingItem.type.includes('output-model')) {
      const targetStorage = inspectingItem.service?.outputs.find((output: any) => output.id === inspectingItem.type)?.targetStorage;
      return (
        <div className="p-2">
          <Form.Group controlId="output-storage">
            <Form.Label>Output Storage</Form.Label>
            <Form.Select size="sm" value={targetStorage || ""} onChange={handleStorageChange}>
              <option value="" hidden disabled>-- select storage --</option>
              {availableStorages?.map(datasource => (
                <option key={datasource.id} value={datasource.id}>{datasource.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      )
    }

    if (inspectingItem.type.includes('input-dataset')) {
      const input = inspectingItem.service.inputs.find((input: any) => input.id === inspectingItem.type);
      const dataset = input?.dataset;

      if (inspectingItem.cascade) return (
        <div className="p-2">
          <Form.Group controlId="cascade-input-columns">
            <Form.Label>Input columns</Form.Label>
            <Form.Control size="sm" placeholder="Service input columns" value={dataset?.columns} onChange={handleCascadeColumnsChange} />
            <Form.Text>Comma-separated names for the columns to feed the algorithm with. Use <u>*</u> to select all the columns.</Form.Text>
          </Form.Group>
        </div>
      )

      const allSelected = dataset?.columns?.filter((col: any) => col.selected).length === dataset?.columns?.filter((col: any) => !col.disabled).length;
      const acceptedTypes = inspectingItem.service.inputs.find((input: any) => input.id === inspectingItem.type)?.acceptedTypes;

      return (
        <div className="p-2">
          {input && (
            <table className="table-transparent">
              {input.datasetMode && (
                <tr>
                  <th scope="col">Input Mode</th>
                  <td className="text-end">
                    <ModeBadge mode={input.datasetMode.toUpperCase()} />
                  </td>
                </tr>
              )}
              {input.datasetType && (
                <tr>
                  <th scope="col">{datasetLabels.singular} Type</th>
                  <td className="text-end">
                    <ModeBadge mode={input.datasetType} />
                  </td>
                </tr>
              )}
            </table>
          )}

          {acceptedTypes && acceptedTypes.length > 0 && (
            <Alert variant="info" className="font-smaller py-2 mb-2">
              <FontAwesomeIcon icon={solid('info-circle')} />
              <span className="ms-2">{serviceLabels.singular} <b>{inspectingItem.service.name}</b> expects the following value types:</span>
              <ul className="mb-0">
                {acceptedTypes.map((type: string) => (
                  <li key={type} className="fw-bold">{type}</li>
                ))}
              </ul>
            </Alert>
          )}

          {!dataset && (
            <Alert variant="warning" className="py-2">
              <FontAwesomeIcon icon={solid('exclamation-circle')} />
              <span className="ms-2">No {datasetLabels.singular} assigned to this input yet.</span>
            </Alert>
          )}

          {dataset && dataset.columns && (
            <Table bordered>
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-center">Type</th>
                  <th className="text-center">
                    <Form.Check type="checkbox" checked={allSelected} onChange={selectAll(allSelected)} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  (dataset.columns as any[]).map((col, index) => (
                    <tr key={col.name}>
                      <td>{col.name}</td>
                      <td className="text-center text-muted">
                        <small>{col.type}</small>
                      </td>
                      <td className="text-center">
                        <Form.Check type="checkbox" checked={col.selected || false} disabled={col.disabled} onChange={handleSelectedChange(index)} />
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </Table>
          )}
        </div>
      )
    }
  }

  const handleShowApplicationNotes = useCallback(() => {
    onShowNotes?.("workflow");
  }, [onShowNotes]);

  const handleShowMarkdown = useCallback(() => {
    onShowServiceMarkdown?.(inspectingItem!.id);
  }, [onShowServiceMarkdown, inspectingItem]);

  const handleShowServiceNotes = useCallback(() => {
    onShowNotes?.("service");
  }, [onShowNotes]);

  const renderElementDetail = () => {
    switch (inspectingItem.type) {
      default:
        return renderAssetSettings();

      case 'dataset':
        return renderDatasetDetail();

      case 'agent':
      case 'mcp':
      case 'batch':
      case 'service':
        return (
          <>
            <Form.Group className="mx-2 mb-3" controlId="label">
              <Form.Label>Label</Form.Label>
              <Form.Control placeholder={`${serviceLabels.singular} Label`} value={serviceLabel || ""} onChange={handleServiceLabelChange} onBlur={handleServiceLabelUpdate} size="sm" />
            </Form.Group>

            <div className="d-flex align-items-center justify-content-between px-2">
              <Form.Label>Version</Form.Label>
              <Badge bg="light" className="border text-dark">
                <FontAwesomeIcon icon={solid('tag')} />
                <span className="ms-2">{inspectingItem.version}</span>
              </Badge>
            </div>

            <UserItem className="sm border p-1 rounded m-2" user={inspectingItem.owner} organization={inspectingItem.organization} />

            <Form.Group className="mb-3 p-1" controlId="bda-app-target">
              <Form.Label>Target</Form.Label>
              <Select
                className="projects-select w-100"
                placeholder="Service Target"
                value={inspectingItem.targetId || inspectingItem.target?.id}
                onChange={handleTargetChange}
                loading={!targetOptions}
                allowClear
              >
                {(targetOptions || []).map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    <h6 className="mb-0">{option.label}</h6>
                    <span className="text-muted">{option.description}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Group>

            <Flex justify="center" className="my-3" gap={8}>
              <Button variant="light" className="border px-3" size="sm" onClick={handleShowMarkdown}>
                <FontAwesomeIcon icon={regular("file-lines")} />
                <span className="ms-2">Documentation</span>
              </Button>
              <AntBadge count={(inspectingItem.notes || []).length}>
                <Button variant="light" className="border px-3" size="sm" onClick={handleShowServiceNotes}>
                  <FontAwesomeIcon icon={solid("comment")} />
                  <span className="mx-2">Notes</span>
                </Button>
              </AntBadge>
            </Flex>

            <ServicePropertiesForm properties={inspectingItem.properties} agentMode={inspectingItem.type === 'agent'} onAgentFieldChange={handleAgentFieldChange} />
          </>
        );

      case 'model':
        return renderModelDetail();
    }
  }

  return (
    <div className="designer-inspector">
      <Row className="gx-0 inspector-header">
        {inspectingItem
          ? (
            <>
              <IconButton className="p-1 px-2 w-auto" onClick={onInspectionCancel} tooltip="Back">
                <FontAwesomeIcon icon={solid('chevron-left')} />
              </IconButton>
              <Col className="overflow-hidden">
                <h6 className="text-uppercase text-truncate mb-0">{inspectingItem.name}</h6>
                {inspectingItem.description && (
                  <Tooltip title={inspectingItem.description} placement="left" mouseEnterDelay={0.5}>
                    <p className="text-alida-secondary font-smaller line-clamp-3 mb-0">{inspectingItem.description}</p>
                  </Tooltip>
                )}
              </Col>
            </>
          )
          : (
            <Col className="overflow-hidden">
              <h6 className="text-uppercase text-truncate mb-0">Create {applicationLabels.singular}</h6>
              <p className="text-alida-secondary mb-0">Fill in {applicationLabels.singular} info.</p>
            </Col>
          )
        }
        <IconButton className="p-1 px-2 w-auto" onClick={onClose}>
          <FontAwesomeIcon icon={solid('times')} />
        </IconButton>
      </Row>

      <div className="inspector-body">
        {inspectingItem
          ? renderElementDetail()
          : <ApplicationForm fromTemplate={fromTemplate} onShowNotes={handleShowApplicationNotes} onApplicationSubmit={onApplicationSubmit} />
        }
      </div>
    </div>
  )
}

export default memo(DesignerInspector);
